import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useCurrentLocation from "../hooks/useCurrentLocation";
import { X, Sun, Moon, Warning, Sparkle, ChatCircleDots, Clock, MapPin, Star, ArrowsClockwise, ArrowCircleRight, CircleNotch } from "@phosphor-icons/react";

const CHOG_COLOR = {
  good: "text-emerald-700 bg-emerald-100 border-emerald-300",
  neutral: "text-amber-800 bg-amber-100 border-amber-300",
  avoid: "text-rose-700 bg-rose-100 border-rose-300",
};

const EVENT_ICON = {
  solar_eclipse: Sun,
  lunar_eclipse: Moon,
  transit: ArrowCircleRight,
  retrograde: ArrowsClockwise,
};

const EVENT_ACCENT = {
  solar_eclipse: { border: "border-amber-400", chip: "bg-amber-100 text-amber-900", icon: "text-amber-700" },
  lunar_eclipse: { border: "border-indigo-300", chip: "bg-indigo-50 text-indigo-900", icon: "text-indigo-600" },
  transit: { border: "border-orange-300", chip: "bg-orange-50 text-orange-900", icon: "text-orange-700" },
  retrograde: { border: "border-rose-300", chip: "bg-rose-50 text-rose-900", icon: "text-rose-700" },
};

function whenLabel(d) {
  if (typeof d !== "number") return "";
  if (d <= 0) return "Today";
  if (d === 1) return "In 1 day";
  if (d < 7) return `In ${d} days`;
  if (d < 30) return `In ${Math.round(d / 7)} weeks`;
  return `In ${Math.round(d / 30)} months`;
}

function EventCard({ event, canPersonalize, onPersonalize, personalized, loading }) {
  const Icon = EVENT_ICON[event.kind] || Sparkle;
  const accent = EVENT_ACCENT[event.kind] || EVENT_ACCENT.transit;
  const showWindow = event.kind === "solar_eclipse" || event.kind === "lunar_eclipse";
  const precautions = personalized?.precautions || event.precautions;
  const suggestions = personalized?.suggestions || event.suggestions;
  return (
    <div className={`card-surface rounded-md p-4 border ${accent.border}`} data-testid={`event-card-${event.kind}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 p-2 rounded-full bg-white/60 ${accent.icon}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h4 className="font-serif-display text-lg leading-tight">{event.title}</h4>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${accent.chip}`}>
              {whenLabel(event.days_away)}
            </span>
          </div>
          <p className="text-xs text-muted mt-1">
            {event.date}
            {event.time ? ` · ${event.time}` : ""}
            {showWindow && event.window_from && event.window_to ? ` · window ${event.window_from}–${event.window_to}` : ""}
            {event.kind === "transit" && event.from_rashi ? ` · from ${event.from_rashi}` : ""}
          </p>

          {event.kind === "transit" && event.effect && (
            <p className="text-sm mt-2.5">{event.effect}</p>
          )}

          {precautions && (
            <div className="mt-2.5">
              <p className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Precautions</p>
              <p className="text-sm text-foreground/85 mt-0.5">{precautions}</p>
            </div>
          )}
          {suggestions && (
            <div className="mt-2">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Suggestions</p>
              <p className="text-sm text-foreground/85 mt-0.5">{suggestions}</p>
            </div>
          )}
          {personalized?.personal_note && (
            <div className="mt-2.5 px-3 py-2 rounded-md bg-amber-50/80 border border-amber-200">
              <p className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1"><Sparkle size={10} weight="fill" /> For your chart</p>
              <p className="text-sm text-amber-900/90 mt-0.5 italic">{personalized.personal_note}</p>
            </div>
          )}

          {canPersonalize && !personalized && (
            <button
              onClick={onPersonalize}
              disabled={loading}
              className="mt-3 text-xs px-3 py-1.5 rounded-full border border-amber-400 text-amber-800 bg-white/60 hover:bg-amber-50 transition-colors disabled:opacity-60 flex items-center gap-1.5"
              data-testid={`personalize-event-btn-${event.kind}-${event.planet || event.type || ""}`}
            >
              {loading ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
              {loading ? "Personalizing…" : "Personalize for my kundali"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PanchangDrawer({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [personalizedById, setPersonalizedById] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [briefLocLabel, setBriefLocLabel] = useState("");
  const currentLoc = useCurrentLocation({ enabled: open });

  useEffect(() => {
    if (!open) return;
    // Prefer the seeker's CURRENT location (browser geolocation) so the
    // brief shows the panchang for where they actually are. Birth coords
    // are the secondary fallback.
    let params;
    let label = "";
    const lagna = user?.kundali?.lagna?.rashi;
    if (currentLoc.status === "ready") {
      params = {
        lat: currentLoc.lat,
        lon: currentLoc.lon,
        timezone: currentLoc.timezone,
        ...(lagna ? { lagna_rashi: lagna } : {}),
      };
      label = currentLoc.city || "your area";
    } else {
      const coords = user?.kundali?.coordinates;
      const tz = user?.birth_details?.timezone;
      if (coords && tz) {
        params = { lat: coords.lat, lon: coords.lon, timezone: tz, ...(lagna ? { lagna_rashi: lagna } : {}) };
        label = (user?.birth_details?.place || "").split(",")[0].trim();
      } else if (lagna) {
        params = { lagna_rashi: lagna };
      }
    }
    setBriefLocLabel(label);
    api.get("/panchang/brief", params ? { params } : undefined)
      .then(({ data }) => setBrief(data))
      .catch(() => {});
  }, [open, user, currentLoc.status, currentLoc.lat, currentLoc.lon, currentLoc.timezone]);

  const canPersonalize = !!user?.kundali;
  const eventKey = (e) => `${e.kind}-${e.planet || e.type || ""}-${e.date}`;

  const handlePersonalize = async (event) => {
    const k = eventKey(event);
    setLoadingId(k);
    try {
      const { data } = await api.post("/panchang/events/personalize", { event });
      setPersonalizedById((prev) => ({ ...prev, [k]: data }));
    } catch (_) {
      // silent fail — static text remains
    } finally {
      setLoadingId(null);
    }
  };

  if (!open) return null;

  // Combine and sort upcoming events by days_away
  const ue = brief?.upcoming_events;
  const upcomingList = ue ? [
    ...(ue.solar_eclipse ? [ue.solar_eclipse] : []),
    ...(ue.lunar_eclipse ? [ue.lunar_eclipse] : []),
    ...(ue.transits || []),
    ...(ue.retrogrades || []),
  ].sort((a, b) => (a.days_away ?? 999) - (b.days_away ?? 999)) : [];

  const cityLabel = briefLocLabel
    || (user?.birth_details?.place ? user.birth_details.place.split(",")[0].trim() : "New Delhi");
  const locSourceTag =
    currentLoc.status === "ready"
      ? "your current location"
      : (user?.birth_details?.place ? "your birth city" : "default");

  return (
    <div className="fixed inset-0 z-[60]" data-testid="panchang-drawer">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        data-testid="drawer-overlay"
      />
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[480px] overflow-y-auto shadow-2xl"
        style={{
          background: "linear-gradient(180deg, #FFE9B5 0%, #FFF6E0 18%, #FFFBF5 100%)",
        }}
      >
        <div className="sticky top-0 px-6 py-4 flex items-center justify-between border-b border-soft" style={{ background: "linear-gradient(90deg, #FFE9B5, #FFD18A)" }}>
          <div>
            <p className="label-eyebrow !text-[#7C2D12] !text-[10px] flex items-center gap-1.5" data-testid="drawer-location-label">
              <MapPin size={11} weight="fill" /> {cityLabel} · {brief?.weekday || ""}
            </p>
            <h3 className="font-serif-display text-2xl mt-1">Today's brief</h3>
            <p className="text-[10px] text-[#7C2D12]/80 mt-0.5" data-testid="drawer-location-source">
              Tuned to {locSourceTag}{currentLoc.status === "loading" ? " · detecting…" : ""}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 transition-colors" data-testid="drawer-close-btn">
            <X size={20} />
          </button>
        </div>

        {!brief ? (
          <div className="p-8 text-muted">Loading the stars...</div>
        ) : (
          <div className="px-6 py-6 space-y-7">
            {/* Headline panchang */}
            <div className="card-surface rounded-md p-5">
              <p className="label-eyebrow flex items-center gap-2"><Star size={12} weight="fill" className="text-gold" />Panchang</p>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <div>
                  <div className="text-muted text-xs">Tithi</div>
                  <div className="font-medium">{brief.tithi_full}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Nakshatra</div>
                  <div className="font-medium">{brief.nakshatra}</div>
                </div>
                <div>
                  <div className="text-muted text-xs flex items-center gap-1"><Sun size={11} weight="fill" />Sunrise</div>
                  <div className="font-medium">{brief.sunrise}</div>
                </div>
                <div>
                  <div className="text-muted text-xs flex items-center gap-1"><Moon size={11} weight="fill" />Sunset</div>
                  <div className="font-medium">{brief.sunset}</div>
                </div>
                {brief.rahu_kaal && (
                  <div className="col-span-2">
                    <div className="text-muted text-xs flex items-center gap-1 text-[#9A3412]"><Warning size={11} weight="fill" />Rahu Kaal</div>
                    <div className="font-medium text-[#9A3412]">{brief.rahu_kaal.from} – {brief.rahu_kaal.to}</div>
                  </div>
                )}
                {brief.abhijit_muhurat && (
                  <div className="col-span-2">
                    <div className="text-muted text-xs flex items-center gap-1 text-emerald-700"><Sparkle size={11} weight="fill" />Abhijit Muhurat</div>
                    <div className="font-medium text-emerald-800">
                      {brief.abhijit_muhurat.from} – {brief.abhijit_muhurat.to}
                      {brief.abhijit_muhurat.skip_today && <span className="text-muted text-xs ml-2">(skip on Wed)</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Best time to chat — CTA */}
            {brief.best_time_to_chat && (
              <div className="card-surface rounded-md p-5 border-2" style={{ borderColor: "rgba(217, 119, 6, 0.35)" }} data-testid="best-time-card">
                <p className="label-eyebrow flex items-center gap-2 text-gold"><Clock size={12} weight="fill" />Best time to chat</p>
                <div className="font-serif-display text-2xl mt-2">
                  {brief.best_time_to_chat.name} window · {brief.best_time_to_chat.from}–{brief.best_time_to_chat.to}
                </div>
                <p className="text-muted text-xs mt-1.5">
                  {brief.best_time_to_chat.starts_in_minutes === 0
                    ? "Window is open now — start a reading."
                    : `Starts in ${brief.best_time_to_chat.starts_in_minutes} minutes.`}
                </p>
                <button onClick={() => { onClose(); navigate("/astrologers"); }} className="btn-primary rounded-full px-5 py-2.5 text-sm mt-4 font-medium flex items-center gap-2" data-testid="best-time-cta">
                  <ChatCircleDots size={14} weight="duotone" /> Find an astrologer
                </button>
              </div>
            )}

            {/* Transit highlights — only if personalized */}
            {brief.transit_highlights?.length > 0 && (
              <div className="card-surface rounded-md p-5" data-testid="transit-highlights-card">
                <p className="label-eyebrow">Transits for your lagna</p>
                <div className="space-y-2.5 mt-3 text-sm">
                  {brief.transit_highlights.map((t) => (
                    <div key={t.planet} className="flex items-baseline gap-2">
                      <span className="text-gold font-semibold w-16 shrink-0">{t.planet}{t.retrograde ? "ᴿ" : ""}</span>
                      <span>
                        in your <span className="font-medium">{t.house === 1 ? "1st" : t.house === 2 ? "2nd" : t.house === 3 ? "3rd" : `${t.house}th`}</span> house —
                        <span className="text-muted ml-1">{t.theme}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!brief.personalized && (
              <div className="card-surface rounded-md p-5 text-center">
                <p className="text-muted text-sm font-light">
                  Add your birth details on the dashboard to unlock personalized transit highlights for your lagna.
                </p>
              </div>
            )}

            {/* Upcoming major events */}
            {upcomingList.length > 0 && (
              <div data-testid="upcoming-events-section">
                <div className="flex items-baseline justify-between mb-3">
                  <p className="label-eyebrow flex items-center gap-2"><Sparkle size={12} weight="fill" className="text-gold" />Upcoming major events</p>
                  {!canPersonalize && (
                    <span className="text-[10px] text-muted italic">Sign in &amp; add birth details for personalization</span>
                  )}
                </div>
                <div className="space-y-3">
                  {upcomingList.slice(0, 8).map((evt) => {
                    const k = eventKey(evt);
                    return (
                      <EventCard
                        key={k}
                        event={evt}
                        canPersonalize={canPersonalize}
                        personalized={personalizedById[k]}
                        loading={loadingId === k}
                        onPersonalize={() => handlePersonalize(evt)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Choghadiya windows */}
            <div className="card-surface rounded-md p-5">
              <p className="label-eyebrow">Day Choghadiya</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {brief.choghadiya_day.map((w, i) => (
                  <div key={i} className={`px-3 py-2 rounded-md border text-xs ${CHOG_COLOR[w.type]}`}>
                    <div className="font-semibold">{w.name}</div>
                    <div className="opacity-80">{w.from}–{w.to}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-surface rounded-md p-5">
              <p className="label-eyebrow">Night Choghadiya</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {brief.choghadiya_night.map((w, i) => (
                  <div key={i} className={`px-3 py-2 rounded-md border text-xs ${CHOG_COLOR[w.type]}`}>
                    <div className="font-semibold">{w.name}</div>
                    <div className="opacity-80">{w.from}–{w.to}</div>
                  </div>
                ))}
              </div>
              <p className="text-muted text-xs mt-3 italic">
                Amrit, Shubh, Labh — auspicious · Char — neutral · Udveg, Rog, Kaal — avoid important work.
              </p>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
